import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useGlossaryTerms, useGlossaryCategories, useGlossarySearch } from '../hooks/useGlossary';
import { GlossaryTerm } from '../types/glossary.types';

export default function GlossaryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [expandedTerms, setExpandedTerms] = useState<Set<number>>(new Set());

  const { data: allTerms, isLoading: loadingTerms } = useGlossaryTerms();
  const { data: categories, isLoading: loadingCategories } = useGlossaryCategories();
  const { data: searchResults, isLoading: searching } = useGlossarySearch(searchQuery);

  const displayedTerms = useMemo(() => {
    if (searchQuery.length >= 2) {
      return searchResults || [];
    }

    if (selectedCategory === 'All') {
      return allTerms || [];
    }

    return (allTerms || []).filter(term => term.category === selectedCategory);
  }, [searchQuery, searchResults, allTerms, selectedCategory]);

  const groupedTerms = useMemo(() => {
    const grouped: Record<string, GlossaryTerm[]> = {};
    displayedTerms.forEach(term => {
      const firstLetter = term.term[0].toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(term);
    });
    return grouped;
  }, [displayedTerms]);

  const toggleTerm = (termId: number) => {
    setExpandedTerms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(termId)) {
        newSet.delete(termId);
      } else {
        newSet.add(termId);
      }
      return newSet;
    });
  };

  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
    setSearchQuery(''); // Clear search when changing category
  };

  if (loadingTerms || loadingCategories) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const categoryTabs = [
    { label: 'All', value: 'All' },
    ...(categories || []).map(cat => ({ label: cat.category, value: cat.category }))
  ];

  return (
    <Box p={3}>
      {/* Header */}
      <Typography variant="h4" component="h1" mb={1}>
        SEO Glossary & Help Center
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Learn essential SEO terms, concepts, and best practices to improve your search engine optimization.
      </Typography>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search glossary terms..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searching && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Category Tabs */}
      {searchQuery.length < 2 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categoryTabs.map(tab => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Results Count */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          {displayedTerms.length} term{displayedTerms.length !== 1 ? 's' : ''} found
          {searchQuery.length >= 2 && ` for "${searchQuery}"`}
        </Typography>
      </Box>

      {/* No Results */}
      {displayedTerms.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No terms found. Try a different search term or category.
        </Alert>
      )}

      {/* Alphabetical Index */}
      {displayedTerms.length > 0 && (
        <Box mb={3}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.keys(groupedTerms).sort().map(letter => (
              <Chip
                key={letter}
                label={letter}
                size="small"
                onClick={() => {
                  document.getElementById(`letter-${letter}`)?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Terms List */}
      {Object.keys(groupedTerms).sort().map(letter => (
        <Box key={letter} id={`letter-${letter}`} mb={4}>
          <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
            {letter}
          </Typography>
          <Stack spacing={2}>
            {groupedTerms[letter].map(term => (
              <TermCard
                key={term.id}
                term={term}
                expanded={expandedTerms.has(term.id)}
                onToggle={() => toggleTerm(term.id)}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

interface TermCardProps {
  term: GlossaryTerm;
  expanded: boolean;
  onToggle: () => void;
}

function TermCard({ term, expanded, onToggle }: TermCardProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        {/* Term Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {term.term}
            </Typography>
            <Chip label={term.category} size="small" color="primary" variant="outlined" />
          </Box>
          <IconButton
            onClick={onToggle}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {/* Short Definition (Always Visible) */}
        <Typography variant="body1" color="text.secondary" mb={2}>
          {term.shortDefinition}
        </Typography>

        {/* Expanded Content */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {/* Detailed Explanation */}
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Detailed Explanation
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {term.detailedExplanation}
            </Typography>
          </Box>

          {/* Why It Matters */}
          {term.whyItMatters && (
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Why It Matters
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {term.whyItMatters}
              </Typography>
            </Box>
          )}

          {/* Example */}
          {term.example && (
            <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Example
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {term.example}
              </Typography>
            </Box>
          )}

          {/* Synonyms */}
          {term.synonyms && term.synonyms.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Also Known As
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {term.synonyms.map((synonym, index) => (
                  <Chip key={index} label={synonym} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          {/* Related Terms */}
          {term.relatedTerms && term.relatedTerms.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Related Terms
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {term.relatedTerms.map((relatedTerm, index) => (
                  <Chip
                    key={index}
                    label={relatedTerm}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Learn More Link */}
          {term.learnMoreUrl && (
            <Box>
              <MuiLink
                href={term.learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <LinkIcon fontSize="small" />
                Learn More
              </MuiLink>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}
